import { Dropdown, InputLine } from '@quero-delivery/quero-components-web';
import { Controller, useFormContext } from 'react-hook-form';
import { celularLabel, cepLabel, cnpjLabel, cpfLabel } from '@/Utils/FnUtils';
import styles from './personal-data-form.module.scss';
import type { DeliverymanFormType } from '../../types';
import { useCities } from '@/_hooks/use-cities';

function PersonalDataForm() {
  const {
    control,
    formState: { errors },
  } = useFormContext<DeliverymanFormType>();

  const { CITIES_OPTIONS, loadingCities } = useCities();

  return (
    <div className={styles.container}>
      <span className={styles.subtitleForm}>Informações pessoais</span>
      <div className={styles.form}>
        <div className={styles.twoLines}>
          <div className={styles.lineLeft}>
            <Controller
              name="nome"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLine
                  autoFocus
                  label="Nome"
                  onChange={event => onChange(event.target.value)}
                  value={value}
                  errorMessage={errors.nome?.message}
                />
              )}
            />
          </div>
          <div className={styles.lineRight}>
            <Controller
              name="senha"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLine
                  label="Senha"
                  onChange={event => onChange(event.target.value)}
                  value={value}
                  errorMessage={errors.senha?.message}
                />
              )}
            />
          </div>
        </div>

        <div className={styles.twoLines}>
          <div className={styles.lineLeft}>
            <Controller
              name="email"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLine
                  label="Email"
                  onChange={event => onChange(event.target.value)}
                  value={value}
                  errorMessage={errors.email?.message}
                />
              )}
            />
          </div>
          <div className={styles.lineRight}>
            <Controller
              name="telefone"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLine
                  label="Telefone"
                  onChange={event => onChange(celularLabel(event.target.value))}
                  value={value}
                  errorMessage={errors.telefone?.message}
                />
              )}
            />
          </div>
        </div>
        <div className={styles.twoLines}>
          <div className={styles.lineLeft}>
            <Controller
              name="cpf"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLine
                  label="CPF"
                  onChange={event => onChange(cpfLabel(event.target.value))}
                  value={value}
                  errorMessage={errors.cpf?.message}
                />
              )}
            />
          </div>
          <div className={styles.lineRight}>
            <Controller
              name="cnpj"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLine
                  label="CNPJ"
                  onChange={event => onChange(cnpjLabel(event.target.value))}
                  value={value}
                  maxLength={18}
                  errorMessage={errors.cnpj?.message}
                />
              )}
            />
          </div>
        </div>
        <div className={styles.twoLines}>
          <div className={styles.lineLeft}>
            <Controller
              name="razaoSocial"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLine
                  label="Razão Social"
                  onChange={event => onChange(event.target.value)}
                  value={value}
                  errorMessage={errors.razaoSocial?.message}
                />
              )}
            />
          </div>
          <div className={styles.lineRight}>
            <Controller
              name="nomeFantasia"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLine
                  label="Nome fantasia"
                  onChange={event => onChange(event.target.value)}
                  value={value}
                  errorMessage={errors.nomeFantasia?.message}
                />
              )}
            />
          </div>
        </div>
        <div className={styles.twoLines}>
          <div className={styles.lineLeft}>
            <Controller
              name="dataNascimento"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLine
                  label="Data de Nascimento"
                  onChange={event => onChange(event.target.value)}
                  value={value}
                  errorMessage={errors.dataNascimento?.message}
                  type="date"
                />
              )}
            />
          </div>
          <div className={styles.lineRight}>
            <Controller
              name="enderecoResidencia.cep"
              control={control}
              render={({ field: { onChange, value } }) => (
                <InputLine
                  label="CEP"
                  onChange={event => onChange(cepLabel(event.target.value))}
                  value={value}
                  errorMessage={errors.enderecoResidencia?.cep?.message}
                />
              )}
            />
          </div>
        </div>

        <div className={styles.twoLines}>
          <div className={styles.lineLeft}>
            <Controller
              name="cidade"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  label="Cidade"
                  placeholder="Selecione a cidade"
                  errorMessage={errors.cidade?.message}
                  onChange={(_, data) => onChange(data.value)}
                  options={CITIES_OPTIONS}
                  loading={loadingCities}
                  value={value}
                  search
                />
              )}
            />
          </div>
          <div className={styles.lineRight}>
            <Controller
              control={control}
              name="enderecoResidencia.bairro"
              render={({ field: { onChange, value } }) => (
                <InputLine
                  label="Bairro"
                  errorMessage={errors.enderecoResidencia?.bairro?.message}
                  value={value}
                  onChange={event => onChange(event.target.value)}
                />
              )}
            />
          </div>
        </div>
        <div className={styles.twoLines}>
          <div className={styles.lineLeft}>
            <Controller
              control={control}
              name="enderecoResidencia.endereco"
              render={({ field: { onChange, value } }) => (
                <InputLine
                  label="Endereço"
                  errorMessage={errors.enderecoResidencia?.endereco?.message}
                  value={value}
                  onChange={event => onChange(event.target.value)}
                />
              )}
            />
          </div>
          <div className={styles.lineRight}>
            <Controller
              control={control}
              name="enderecoResidencia.numero"
              render={({ field: { onChange, value } }) => (
                <InputLine
                  label="Número"
                  errorMessage={errors.enderecoResidencia?.numero?.message}
                  value={value}
                  onChange={event => onChange(event.target.value)}
                />
              )}
            />
          </div>
        </div>
        <div className={styles.twoLines}>
          <div className={styles.lineLeft}>
            <Controller
              control={control}
              name="enderecoResidencia.referencia"
              render={({ field: { onChange, value } }) => (
                <InputLine
                  label="Referência"
                  errorMessage={errors.enderecoResidencia?.referencia?.message}
                  value={value}
                  onChange={event => onChange(event.target.value)}
                />
              )}
            />
          </div>
          <div className={styles.lineRight}>
            <Controller
              control={control}
              name="enderecoResidencia.complemento"
              render={({ field: { onChange, value } }) => (
                <InputLine
                  label="Complemento"
                  errorMessage={errors.enderecoResidencia?.complemento?.message}
                  value={value}
                  onChange={event => onChange(event.target.value)}
                />
              )}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export { PersonalDataForm };
